/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Bandwidth options of a transfer (src/doc/swift/bandwidth-and-readiness.md, section 1), with the
 * backend's rules mirrored from coriolis/bandwidth.py so the editor can validate as the API does
 * and draw which limit applies when:
 *
 *   - days: the days a window starts on (Monday first), default every day;
 *   - start/end "HH:MM" (24 h) in the window's zone; end before start runs overnight, end equal
 *     to start is the whole day; end is exclusive;
 *   - several matching windows: the one covering the fewest minutes a week wins, then the first;
 *   - outside every window, bandwidth_limit_mibps applies; 0 means unlimited.
 */

import type { BandwidthDay, BandwidthRule } from "@src/@types/Cutover";

export const LIMIT_KEY = "bandwidth_limit_mibps";
export const SCHEDULE_KEY = "bandwidth_schedule";
export const BANDWIDTH_KEYS = [LIMIT_KEY, SCHEDULE_KEY];

export const DAYS: BandwidthDay[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const DAY_LABELS: { [d in BandwidthDay]: string } = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};
export const WEEKDAYS: BandwidthDay[] = ["mon", "tue", "wed", "thu", "fri"];
export const WEEKEND: BandwidthDay[] = ["sat", "sun"];

export const MAX_RULES = 32;
export const MAX_LIMIT_MIBPS = 100000;
export const DEFAULT_TZ = "Asia/Kolkata";
const HHMM = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export const TIME_ZONES = [
  "Asia/Kolkata",
  "UTC",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
];

/** 1 MiB/s = 8.388608 Mbit/s. */
export const mibpsToMbit = (mibps: number) => mibps * 8.388608;
export const mbitToMibps = (mbit: number) => mbit / 8.388608;

export const formatLimit = (mibps: number | null | undefined) =>
  !mibps ? "Unlimited" : `${Number.isInteger(mibps) ? mibps : mibps.toFixed(1)} MiB/s`;

export const minutesOf = (hhmm: string): number | null => {
  const m = HHMM.exec(String(hhmm || "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/** Minutes a window lasts each time it runs (end == start: the whole day). */
export const windowLength = (rule: BandwidthRule) => {
  const s = minutesOf(rule.start);
  const e = minutesOf(rule.end);
  if (s === null || e === null) return 0;
  return (e - s + 1440) % 1440 || 1440;
};
export const isOvernight = (rule: BandwidthRule) => {
  const s = minutesOf(rule.start);
  const e = minutesOf(rule.end);
  return s !== null && e !== null && e < s;
};
export const isWholeDay = (rule: BandwidthRule) => {
  const s = minutesOf(rule.start);
  return s !== null && s === minutesOf(rule.end);
};

const ruleDays = (rule: BandwidthRule): BandwidthDay[] =>
  rule.days && rule.days.length ? rule.days : DAYS;

export const isKnownZone = (tz: string) => {
  if (!tz) return true; // the API defaults to UTC
  // The API wants IANA names ("Asia/Kolkata", "UTC"); browsers also accept abbreviations such as
  // "IST", which the API refuses.
  if (tz !== "UTC" && tz !== "GMT" && !/^[A-Za-z]+\/[A-Za-z0-9_+\-/]+$/.test(tz)) return false;
  try {
    Intl.DateTimeFormat("en-GB", { timeZone: tz });
    return true;
  } catch (e) {
    return false;
  }
};

export type RuleErrors = {
  days?: string;
  start?: string;
  end?: string;
  limit?: string;
  tz?: string;
};
export type BandwidthErrors = {
  limit?: string;
  schedule?: string;
  rules: RuleErrors[];
};

const limitError = (value: any, what: string): string | undefined => {
  if (value === "" || value === null || value === undefined || Number.isNaN(Number(value))) {
    return `${what} must be a number of MiB/s.`;
  }
  const n = Number(value);
  if (n < 0 || n > MAX_LIMIT_MIBPS) return `${what} must be between 0 and ${MAX_LIMIT_MIBPS} MiB/s.`;
  return undefined;
};

export const validateRule = (rule: BandwidthRule): RuleErrors => {
  const out: RuleErrors = {};
  if (rule.days && !rule.days.length) out.days = "Choose at least one day.";
  if (minutesOf(rule.start) === null) out.start = "Use HH:MM (24 h).";
  if (minutesOf(rule.end) === null) out.end = "Use HH:MM (24 h).";
  const limit = limitError(rule.limit_mibps, "The limit");
  if (limit) out.limit = limit;
  if (rule.tz && !isKnownZone(rule.tz)) out.tz = `Unknown time zone '${rule.tz}'. Use an IANA name such as Asia/Kolkata.`;
  return out;
};

export const validateBandwidth = (limit: any, schedule: BandwidthRule[]): BandwidthErrors => {
  const out: BandwidthErrors = { rules: schedule.map(validateRule) };
  const l = limitError(limit, "The limit");
  if (l) out.limit = l;
  if (schedule.length > MAX_RULES) out.schedule = `At most ${MAX_RULES} windows.`;
  return out;
};

export const hasErrors = (errors: BandwidthErrors) =>
  Boolean(errors.limit || errors.schedule || errors.rules.some(r => Object.keys(r).length));

/** "Mon–Fri", "Every day", "Sat, Sun", "Mon, Wed, Fri". */
export const describeDays = (days?: BandwidthDay[]) => {
  const list = days && days.length ? DAYS.filter(d => days.includes(d)) : DAYS;
  if (list.length === 7) return "Every day";
  if (list.join() === WEEKDAYS.join()) return "Mon–Fri";
  if (list.join() === WEEKEND.join()) return "Sat–Sun";
  // A run of consecutive days: "Tue–Thu".
  const idx = list.map(d => DAYS.indexOf(d));
  const consecutive = idx.length > 2 && idx.every((v, i) => i === 0 || v === idx[i - 1] + 1);
  if (consecutive) return `${DAY_LABELS[list[0]]}–${DAY_LABELS[list[list.length - 1]]}`;
  return list.map(d => DAY_LABELS[d]).join(", ");
};

/** "Mon–Fri 09:00–18:00 Asia/Kolkata: 50 MiB/s". */
export const describeRule = (rule: BandwidthRule) => {
  const time = isWholeDay(rule) ? "all day" : `${rule.start}–${rule.end}${isOvernight(rule) ? " (overnight)" : ""}`;
  return `${describeDays(rule.days)} ${time} ${rule.tz || "UTC"}: ${formatLimit(Number(rule.limit_mibps))}`;
};

// ------------------------------------------------------------------ evaluation

const formatters: { [tz: string]: Intl.DateTimeFormat } = {};
const WEEKDAY_INDEX: { [k: string]: number } = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

/** Monday = 0 day and minute of the day of an instant in a zone. */
export const zoneParts = (tz: string, ms: number): { day: number; minute: number } => {
  const zone = tz || "UTC";
  if (!formatters[zone]) {
    formatters[zone] = new Intl.DateTimeFormat("en-GB", {
      timeZone: zone,
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  }
  const parts = formatters[zone].formatToParts(new Date(ms));
  const get = (t: string) => parts.find(p => p.type === t)?.value || "0";
  const hour = Number(get("hour")) % 24;
  return { day: WEEKDAY_INDEX[get("weekday")] ?? 0, minute: hour * 60 + Number(get("minute")) };
};

export const ruleMatches = (rule: BandwidthRule, ms: number) => {
  const start = minutesOf(rule.start);
  const end = minutesOf(rule.end);
  if (start === null || end === null || (rule.tz && !isKnownZone(rule.tz))) return false;
  const days = ruleDays(rule).map(d => DAYS.indexOf(d));
  const { day, minute } = zoneParts(rule.tz || "UTC", ms);
  if (start === end) return days.includes(day);
  if (start < end) return days.includes(day) && minute >= start && minute < end;
  return (days.includes(day) && minute >= start) || (days.includes((day + 6) % 7) && minute < end);
};

/** The limit in effect at an instant and the window that set it (null: the default limit). */
export const resolveLimit = (limit: number, schedule: BandwidthRule[], ms: number) => {
  let best: { index: number; weekly: number } | null = null;
  schedule.forEach((rule, index) => {
    if (Object.keys(validateRule(rule)).length || !ruleMatches(rule, ms)) return;
    const weekly = windowLength(rule) * ruleDays(rule).length;
    if (!best || weekly < best.weekly) best = { index, weekly };
  });
  if (!best) return { limit: Number(limit) || 0, rule: null as number | null };
  const b = best as { index: number; weekly: number };
  return { limit: Number(schedule[b.index].limit_mibps) || 0, rule: b.index as number | null };
};

export type PreviewSegment = { start: number; end: number; limit: number; rule: number | null };

/**
 * Which limit applies when, over the current week in `displayTz`: 7 rows (Monday first) of
 * segments in minutes of the day. Sampled every `slot` minutes.
 */
export const weekPreview = (
  limit: number,
  schedule: BandwidthRule[],
  displayTz: string,
  now = Date.now(),
  slot = 15,
): PreviewSegment[][] => {
  const { day, minute } = zoneParts(displayTz, now);
  const weekStart = now - (day * 1440 + minute) * 60000 - (now % 60000);
  const rows: PreviewSegment[][] = [];
  for (let d = 0; d < 7; d += 1) {
    const row: PreviewSegment[] = [];
    for (let m = 0; m < 1440; m += slot) {
      const at = weekStart + (d * 1440 + m) * 60000;
      const r = resolveLimit(limit, schedule, at);
      const last = row[row.length - 1];
      if (last && last.limit === r.limit && last.rule === r.rule) last.end = m + slot;
      else row.push({ start: m, end: m + slot, limit: r.limit, rule: r.rule });
    }
    rows.push(row);
  }
  return rows;
};

/** The schedule as the API takes it: trimmed times, numbers, days in week order. */
export const cleanSchedule = (schedule: BandwidthRule[]): BandwidthRule[] =>
  schedule.map(rule => {
    const out: BandwidthRule = {
      start: String(rule.start).trim(),
      end: String(rule.end).trim(),
      limit_mibps: Number(rule.limit_mibps) || 0,
      tz: rule.tz || DEFAULT_TZ,
    };
    const days = rule.days && rule.days.length ? DAYS.filter(d => rule.days!.includes(d)) : DAYS;
    out.days = days;
    return out;
  });

export const newRule = (tz = DEFAULT_TZ): BandwidthRule => ({
  days: [...WEEKDAYS],
  start: "09:00",
  end: "18:00",
  limit_mibps: 50,
  tz,
});

/** The bandwidth options found in a source environment (the API's defaults when absent). */
export const readBandwidth = (env?: { [k: string]: any } | null): { limit: number; schedule: BandwidthRule[] } => ({
  limit: Number(env?.[LIMIT_KEY]) || 0,
  schedule: Array.isArray(env?.[SCHEDULE_KEY]) ? (env![SCHEDULE_KEY] as BandwidthRule[]).map(r => ({ ...r })) : [],
});
